<template>
  <div>
    <div class="breadcrumbs"></div>
    <!-- Page title and right side buttons  --->
    <PageTitle pageHeading="Tickets" :rightsideButton="true">
      <div class="body-2 d-flex justify-end right-side-filter">
        <div class="default-view d-flex" v-if="selectedTicketsData.length === 0">
          <FilterDrawer
            ref="ticketFilterDrawer"
            v-if="loadFilter"
            @applyFilter="getTicketList('filter')"
            @onViewListLoad="refreshList"
            :currentTab="activeTabName"
          ></FilterDrawer>
          <v-menu
            v-if="$can('create','tickets') || $can('create','contacts')"
            offset-y
            v-model="buttonToggle"
            min-width="185px"
            :left="true"
            attach
            class="create-ticket-menu"
          >
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" dark class="font-weight-bold px-2">
              <span class="pl-2 pr-3"> New </span>
              <v-icon v-if="!buttonToggle" color="#fff"> mdi-menu-down </v-icon>
              <v-icon v-if="buttonToggle" color="#fff"> mdi-menu-up </v-icon>
            </v-btn>
          </template>
          <v-list class="pa-0">
            <v-list-item id="new-ticket-link" link :to="{ name: 'new-ticket' }" v-if="$can('create','tickets')">
              <v-list-item-title> New Ticket </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="$can('create','contacts')"
              link
              id="new-contact-link"
              :to="{ name: 'create-contact' }"
            >
              <v-list-item-title> New Contact </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div v-else class="row-selected-view d-flex">
        <div class="action-buttons d-flex" v-if="$can('create','tickets')">
          <template v-if="this.selectedTicketsData.length >= 2">
            <bulk-action-drawer
              ref="ticketFilterDrawer"
              :selected-tickets="selectedTicketsData"
              @getCustomViewList="getTicketList()"
              @resetSelectedTickets="selectedTicketsData = []"
            ></bulk-action-drawer>
          </template>
          <template v-if="!disableActionBtn">
            <v-btn
              class="mr-4 pl-2 font-weight-bold"
              id="assign-btn"
              v-track="'assign-ticket-btn'"
              @click="assignTicketToggle()"
              outlined
            >
              <v-icon color="#000" class="mr-1">mdi-clipboard-account</v-icon>
              ASSIGN
            </v-btn>
            <v-btn
              id="close-btn"
              class="mr-4 pl-2 font-weight-bold"
              v-track="'close-ticket-btn'"
              outlined
              @click="closeAction('', '')"
            >
              <v-icon color="#000" class="mr-1">mdi-close-circle</v-icon>
              CLOSE
            </v-btn>
          </template>
          <v-btn
            id="delete-ticket"
            class="mr-4 pl-2 font-weight-bold"
            v-track="'delete-ticket-btn'"
            outlined
            @click="onDelete()"
          >
            <v-icon color="#000" class="mr-1">mdi-delete</v-icon>
            DELETE
          </v-btn>
        </div>
      </div>
      </div>
    </PageTitle>
    <v-row class="tabs-row" no-gutters>
      <v-col cols="12" class="pb-0">
        <div class="d-flex align-center view-container" ref="container">
        <v-tooltip
          bottom
          nudge-top="10"
          nudge-right="50"
          close-on-content-click
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              class="d-md-flex"
              v-track="'open-edit-view-click'"
              v-on="on"
              @click="openCard"
            >
            <span>
              <v-img src="@/assets/img/filter-view.svg" />
            </span>
            </v-btn>
          </template>
          <span class="mr-2">Ticket Views</span>
        </v-tooltip>
        <CustomizeView
          ref="customizeView"
          :userPreferences="userTabsPreferences"
          @open-drawer="openAddNewViewDrawer"
          @edit-filter-view="editFilterView"
          @delete-filter-view="deleteFilterView"
          :key="loadCustomViews"
        />
        <!---- different tabs section ----->
        <v-tabs
          v-model="activeTab" 
          color="#03b6fc"
          :mobile-breakpoint="960"
          class="border-bottom tabs-section"
          show-arrows
        >
          <v-tab
            v-for="item in currentTabs"
            :key="item.id"
            :to="{ name: item.routerName, params: { id: item.id }  }"
            :id="item.id"
            class="body-2"
            >
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ getModified(item.name, 22) }}
                </span>
              </template>
              <span>{{ item.name }}</span>
            </v-tooltip>
          </v-tab>
        </v-tabs>
        </div>
      </v-col>
    </v-row>
    <ColumnFilter
        ref="columnFilterDrawer"
        :key="loadComponent"
        :columnHeaders="ticketHeaders"
        @filterColumns="filterSelectedColumns"
    />
    <!----- ticket list page table ---->
    <v-data-table
      id="ticket-list-table"
      :show-select="$can('create','tickets')"
      :headers="headers"
      :items="ticketList"
      :options.sync="pagination"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 75, 100],
      }"
      :mobile-breakpoint="960"
      :items-per-page="perPage"
      :loading="loading"
      item-key="id"
      class="elevation-2 mt-10 ticket-list-table"
      @input="getSelectData"
      checkbox-color="#45B6F8"
      :value="selectedTicketsData"
      :item-class="rowClass"
      @click:row="rowClickHandler"
    >
      <template v-slot:header.action="{ header }">
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn icon class="filter-icon" v-track="'column-filter-btn'">
                <v-icon @click="openColumnDrawer">
                  mdi-filter-variant
                </v-icon>
              </v-btn>
            </span>
          </template>
          <span>Column Filter</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.id`]="{ item }">
        <div class="d-flex">
          <router-link
            v-track="'ticket-detail-page-link'"
            :to="{ name: 'ticket-details', params: { id: item.id } }"
          >
            #{{ item.id }}
          </router-link>
          <!-- <v-tooltip bottom v-if="item.is_parent">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" color="#FF62C1" class="pl-1">
                mdi-link-variant
              </v-icon>
            </template>
            Parent Ticket
          </v-tooltip> -->
        </div>
      </template>

      <template v-slot:[`item.contact_name`]="{ item }">
       <div class="cursor-pointer link-color">
          <span
            @click.stop="redirectToContact(item)"
            v-track="'contact-dashboard-link'"
            v-if="item.contact_name.trim() && item.contact_name.length >= 0"
          >
            {{ item.contact_name | upperCaseFirst }}
          </span>
          <span
            v-track="'contact-dashboard-link'"
            v-else
            @click.stop="redirectToContact(item)"
          >
            --
          </span>
        </div>
      </template>

      <template v-slot:[`item.subject`]="{ item }">
        <v-tooltip left max-width="50%">
          <template v-slot:activator="{ on }">
            <router-link
              :to="{ name: 'ticket-details', params: { id: item.id } }"
              class="body-1 word-break text-ellipsis cursor-pointer"
              v-track="'ticket-detail-page-link'"
            >
              <span v-on="on"
                >{{ item.subject | upperCaseFirst }}
              </span></router-link
            >
          </template>
          <span class="d-block overflow-auto word-break">{{ item.subject | upperCaseFirst }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.tags`]="{ item }">
        <div class="menu-container tags" v-if="'tags' in item && item.tags.length > 0">
          <v-chip
            v-for="(tags, index) in item.tags ? item.tags.slice(0, 1) : []"
            :key="index"
            class="ma-1"
            color="rgb(208, 218, 232)"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ getModified(tags.name,7) }}
                </span>
              </template>
              <span>{{ tags.name }}</span>
            </v-tooltip>
          </v-chip>
          <v-menu 
            offset-y
            :attach="enableAttach() ? `#attach-tag-${item.id}`:false"
            :top="enableTop(item.id)"
          >
            <template v-slot:activator="{ on, attrs }">
              <a
                v-if="item.tags.length > 1"
                class="ma-1"
                :id="'attach-tag-'+item.id"
                v-bind="attrs"
                v-on="on"
              >
                +{{ item.tags.length - 1 }} more
              </a>
            </template>

            <v-list class="menu-list">
              <v-list-item-group>
                <v-list-item
                  v-for="(tags, index) in item.tags.slice(1)"
                  :key="index"
                >
                {{ getModified(tags.name,10) }}
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        {{ item.created_at | formatDate }}
      </template>

      <template v-slot:[`item.requesters_last_response_at`]="{ item }">
        {{ item.requesters_last_response_at | formatDate }}
      </template>
      
      <template v-slot:[`item.group_name`]="{ item }">
        <span v-if="item.status === 'closed'">
          {{ !item.group_name ? "--" : item.group_name | upperCaseFirst }}
        </span>

        <v-menu
          offset-y
          v-else
          class="cursor-pointer"
          z-index="999"
          content-class="scroll-dropdown"
        >
          <template v-slot:activator="{ on, attrs }">
            <span
              class="d-flex"
              v-bind="attrs"
              v-on="on"
              @click="identifyFields('groups')"
            >
              <span class="word-break text-ellipsis">
                {{
                  !item.group_name ? "--" : item.group_name | upperCaseFirst
                }}</span
              >

              <v-icon v-if="attrs['aria-expanded'] == 'false'">
                mdi-menu-down
              </v-icon>

              <v-icon v-if="attrs['aria-expanded'] == 'true'">
                mdi-menu-up
              </v-icon>
            </span>
          </template>
          <v-list
            style="max-height: 240px; max-width: 200px"
            class="py-0 action-dropdown overflow-y-auto"
            v-scroll.self="indexItemsScroll"
          >
            <v-list-item-group>
              <v-list-item
                class="cursor-pointer"
                v-for="(group, index) in associatedField.groups.list"
                :key="index"
                @click="
                  item.group_name !== group.name
                    ? performAction(item, 'group_id', group.id, 'index-page')
                    : ''
                "
                :class="
                  item.group_name == group.name ? 'v-list-item--active' : ''
                "
              >
                <v-list-item-title>{{ group.name }}</v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:[`item.ticket_type_name`]="{ item }">
        <div class="menu-container">
          <span
            v-if="item.status === 'closed'"
            class="text-truncate d-block"
            style="max-width: 100px"
          >
            {{
              !item.ticket_type_name
                ? "--"
                : item.ticket_type_name | upperCaseFirst
            }}
          </span>
          <v-menu
            v-else
            :attach="enableAttach() ? `#attach-type-${item.id}`:false"
            :top="enableTop(item.id)"
            min-width="185px"
            offset-y
            class="cursor-pointer"
            content-class="scroll-dropdown"
            @input="identifyFields('ticket_types')"
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="d-flex" :id="'attach-type-'+item.id">
                <span class="word-break text-ellipsis">{{
                  !item.ticket_type_name
                    ? "--"
                    : item.ticket_type_name | upperCaseFirst
                }}</span>

                <v-icon v-if="attrs['aria-expanded'] == 'false'"  v-show="$can('create','tickets')">
                  mdi-menu-down
                </v-icon>

                <v-icon v-if="attrs['aria-expanded'] == 'true'"  v-show="$can('create','tickets')">
                  mdi-menu-up
                </v-icon>
              </span>
            </template>
            <v-list
              class="menu-list py-0 action-dropdown overflow-y-auto"
              v-scroll.self="indexItemsScroll"
              v-if="$can('create','tickets')"
            >
              <v-list-item-group>
                <v-list-item v-if="item.ticket_type_name" @click="unAssign('type',item)" block class="unassign-btn subtitle-1 primary--font">
                  Unassign
                </v-list-item>
                <v-list-item
                  class="cursor-pointer"
                  v-for="(type, index) in filteredTypesList"
                  :key="index"
                  @click="
                    item.ticket_type_name !== type.name
                      ? performAction(
                          item,
                          'ticket_type_id',
                          type.id,
                          'index-page'
                        )
                      : ''
                  "
                  :class="
                    item.ticket_type_name == type.name
                      ? 'v-list-item--active'
                      : ''
                  "
                >
                  <v-list-item-title>{{ type.name }}</v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.channel`]="{ item }">
        <span>{{ item.channel.replace("_", " ") | upperCaseFirst }} </span>
      </template>

      <template v-slot:[`item.agent_name`]="{ item }">
        <div class="menu-container">
          <span v-if="item.status === 'closed'">
            {{ item.agent_name | upperCaseFirst }}
          </span>
          <v-menu
            offset-y
            :attach="enableAttach() ? `#attach-agent-${item.id}`:false"
            class="cursor-pointer"
            :top="enableTop(item.id)"
            min-width="185px"
            v-else
            :close-on-content-click="hideAgentDropdown"
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="custom-dropdown" :id="'attach-agent-'+item.id">
                {{
                  !item.agent_id
                    ? "--"
                    : item.agent_name | upperCaseFirst
                }}
                <v-icon v-if="attrs['aria-expanded'] == 'false'" v-show="$can('create','tickets')">
                  mdi-menu-down
                </v-icon>
                <v-icon v-if="attrs['aria-expanded'] == 'true'" v-show="$can('create','tickets')">
                  mdi-menu-up
                </v-icon>
              </span>
            </template>
            <v-list class="menu-list py-0" v-if="$can('create','tickets')">
              <v-list-item @click="hideAgentDropdown = false" class="pa-0">
                <v-text-field
                  class="search-agent px-2"
                  hide-details
                  autofocus
                  v-model="enteredAgent"
                  @input="getAgentList(enteredAgent)"
                ></v-text-field>
              </v-list-item>
              <v-list-item-group>
                <v-list-item v-if="item.agent_id" @click="unAssign('agent',item)" block class="unassign-btn subtitle-1 primary--font">
                  Unassign
                </v-list-item>
                <template v-if="filteredAgentList.length">
                  <v-list-item
                    class="cursor-pointer"
                    v-for="agent in filteredAgentList"
                    :key="agent.id"
                    @click="checkRelevantAction(item, 'agent_id', agent.id, 'index-page')"
                    :class="{'v-list-item--active' : item.agent_name === agent.agent_name}"
                  >
                    <v-list-item-title>{{ agent.agent_name }}</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item v-else>
                  <v-list-item-title> No data found ! </v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <div class="menu-container">
          <span v-if="item.status === 'closed'">
            {{ item.status | upperCaseFirst }}
          </span>
          <v-menu 
              v-else
              :top="enableTop(item.id)"
              :attach="enableAttach() ? `#attach-status-${item.id}`:false"
              offset-y 
              class="cursor-pointer">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="custom-dropdown" :id="'attach-status-'+item.id">
                {{ item.status.replace("_", " ") | upperCaseFirst }}
                <v-icon v-if="attrs['aria-expanded'] == 'false'"  v-show="$can('create','tickets')">
                  mdi-menu-down
                </v-icon>

                <v-icon v-if="attrs['aria-expanded'] == 'true'"  v-show="$can('create','tickets')">
                  mdi-menu-up
                </v-icon>
              </span>
            </template>
            <v-list class="py-0 menu-list"  v-if="$can('create','tickets')">
              <v-list-item-group>
                <v-list-item
                  class="cursor-pointer"
                  v-for="(status, index) in filterStatusList"
                  :key="index"
                  @click="
                    checkRelevantAction(item, 'status', status, 'index-page')
                  "
                  :class="item.status == status.name ? 'v-list-item--active' : ''"
                >
                  <v-list-item-title>{{
                    status.name.replace("_", " ")
                  }}</v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.priority`]="{ item }">
        <div class="menu-container">
          <span v-if="item.status === 'closed'">
            <v-icon :color="priorityColor(item.priority)" size="10px" class="pr-2">mdi-checkbox-blank-circle</v-icon>
            {{ item.priority | upperCaseFirst }}
          </span>
          <v-menu
              :top="enableTop(item.id)"
              offset-y
              v-else
              :attach="enableAttach() ? `#attach-priority-${item.id}`:false"
              class="cursor-pointer"
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="custom-dropdown" :id="'attach-priority-'+item.id">
                <v-icon :color="priorityColor(item.priority)" size="10px" class="pr-2">mdi-checkbox-blank-circle</v-icon>
                {{ item.priority | upperCaseFirst }}
                <v-icon v-if="attrs['aria-expanded'] == 'false'" v-show="$can('create','tickets')">
                  mdi-menu-down
                </v-icon>

                <v-icon v-if="attrs['aria-expanded'] == 'true'" v-show="$can('create','tickets')">
                  mdi-menu-up
                </v-icon>
              </span>
            </template>
            <v-list class="py-0" v-if="$can('create','tickets')">
              <v-list-item-group>
                <v-list-item
                  class="cursor-pointer"
                  v-for="(priority, index) in associatedField.priorities"
                  :key="index"
                  @click="
                    item.priority !== priority.name
                      ? performAction(
                          item,
                          'priority',
                          priority.value,
                          'index-page'
                        )
                      : ''
                  "
                  :class="
                    item.priority == priority.name ? 'v-list-item--active' : ''
                  "
                >
                  <v-list-item-title class="text-capitalize">
                    <v-icon :color="priorityColor(priority.name)" size="10px" class="pr-2">mdi-checkbox-blank-circle</v-icon>
                    {{ priority.name }}</v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>
      <!-- <template v-slot:[`item.action`]="{}">
          <v-icon> mdi-filter-variant </v-icon>
      </template> -->
    </v-data-table>

    <!-- Remove selected ticket dialog -->
    <dialog-box
      v-bind="dialog.attrs"
      v-on="dialog.on"
    />

    <!-- Assign ticket to agent dialog -->
    <v-dialog
      v-model="assignTicketDialog"
      max-width="600"
      persistent
      content-class="customize-dialog"
    >
      <v-card class="pa-4">
        <v-card-title class="text-h6 font-weight-600">
          <span class="secondary--font text-capitalize">
            Assign Ticket (<span
              v-for="(ticket, index) in selectedTicketsData"
              :key="ticket.id"
            >
              <span v-if="index >= 1">,</span>#{{ ticket.id }}</span
            >)
          </span>
        </v-card-title>

        <v-card-text class="body-2 px-4 mt-5">
          <v-form class="customize-select" ref="form">
            <v-autocomplete
              outlined
              label="Select Agent"
              v-model="selectedAgent"
              :items="associatedField.agentList"
              item-text="agent_name"
              item-value="id"
              z-index="999"
              attach
              :no-filter="true"
              :search-input.sync="searchAgentKeyword"
            ></v-autocomplete>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="assignTicketDialog = false"
            class="font-weight-bold"
            v-track="'cancel-assign-ticket'"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="selectedAgent == ''"
            @click="assignTicket()"
            class="font-weight-bold"
            v-track="'confirm-assign-ticket'"
            :dark="selectedAgent !== ''"
          >
            Assign
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- close ticket dialog -->
    <CloseTicketDialog 
      :closeDialog="closeDialog" 
      :selectedTicketsData="selectedTicketsData"
      :checkSelectedTicket="checkSelectedTicket"
      @confirmClose="confirmClose"
      @updateDialog="updateCloseDialog"
    />

    <!-- Delete Custom View Dialog -->
    <DeleteViewDialog @evtOnCloseDeleteViewDialog="onCloseDeleteViewDialog" ref="deleteViewDialog"/>

    <v-overlay
      :value="pageLoader"
      opacity="0.2"
    >
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
  </div>
</template>

<script>
import CustomizeView from "@/components/tickets/CustomizeView.vue"
import PageTitle from "@/components/shared/PageTitle.vue";
import FilterDrawer from "@/components/tickets/FilterDrawer";
import { $tickets } from "@/services/Index";
import { $filter } from "@/services/Index";
import { ticketMixin } from "@/mixins/ticket";
import DeleteViewDialog from "@/components/tickets/DeleteViewDialog";
import { mapGetters } from "vuex";
import commonFunctions from "@/commonFunctions";
import ColumnFilter from "@/components/tickets/ColumnFilter.vue";
import { $preferences } from "@core/services/";
import CloseTicketDialog from "@/components/tickets/CloseTicketDialog";
import BulkActionDrawer from "@/components/tickets/BulkActionDrawer";

export default {
  name: "TicketView",
  mixins: [ticketMixin],
  components: {
    PageTitle,
    FilterDrawer,
    DeleteViewDialog,
    CustomizeView,
    ColumnFilter,
    CloseTicketDialog,
    BulkActionDrawer
  },
  data() {
    return {
      loadFilter: true,
      singleSelect: false,
      buttonToggle: false,
      ticketList: [],
      perPage: 10,
      page: 1,
      totalItems: 0,
      pagination: {},
      sortingToggle: false,
      selectedTicketsData: [],
      currentStatus: null,
      selectedClosedTicket: [],
      activeTab: "",
      globalSearchKeyword: "",
      assignTicketDialog: false,
      selectedAgent: "",
      agentList: [],
      currentAgentId: null,
      filterViews: [],
      pageLoader: false,
      showMoreTags:false,
      selectedListView: null,
      hideAgentDropdown: false,
      enteredAgent: '',
      dialog: {
        attrs: {
          show: false,
          title: "Delete Ticket",
          loading: false,
          confirmBtn: true,
          confirmationText: "Delete",
          maxWidth: 550,
          type: 'warning',
          message: `Note: You can restore deleted tickets from “Trash” under Ticket Views`,
          image: 'trash-access-direction.svg'
        },
        on: {
          confirm: this.onConfirm,
          cancel: () => {
            this.dialog.attrs.show = false;
          },
        },
      },
    };
  },
  computed: {
    activeViewName(){
      let filter = {}
      if(this.selectedFilterId) {
        filter = this.tabsPreferences.find(filter => filter.id === this.selectedFilterId);
      } else {
        filter = this.tabsPreferences.find(filter => filter.routerName === this.activeTabName);
      }
      return filter && filter.label || '';
    },
    checkSelectedTicket() {
      if (this.selectedTicketsData.length !== 0) {
        return this.selectedTicketsData.length;
      } else {
        return 0;
      }
    },

    disableActionBtn() {
      let value = true;
      if (this.selectedTicketsData.length >= 1) {
        this.selectedTicketsData.forEach((item) => {
          if (item.status !== "closed") {
            value = false;
          }
        });
      }
      return value;
    },
    filterStatusList() {
      let filteredList = this.associatedField.statusList.filter((status) => {
        return status.name !== "new";
      });
      return filteredList;
    },
    statusFilter() {
      return this.$route.query.status
    },
    activeTabName() {
      return this.$route.name
    },  
    currentAccountId() {
      return this.$store.getters["auth/account"].id;
    },
    filterAccountId(){
      return this.$store.getters["tickets/filterAccountId"];
    },
    filterAction() {
      return this.headersMap.find(item => item.value === 'action');
    }
  },
  mounted() {
    //get user preferences from identity
    this.getPreferences();
    /** removing data from session if exist */
    if (sessionStorage.getItem("new-contact") !== null)
      sessionStorage.removeItem("new-contact");
    if (sessionStorage.getItem("ticket-data") !== null)
      sessionStorage.removeItem("ticket-data");
  },
  watch: {
    pagination: {
      handler(newVal, oldVal) {
        /** prevent this check to invoked function on page load */
        if (oldVal.itemsPerPage) {
          this.checkRouterAndAppliedFilter();
        }
      },
      deep: true,
    },
    /*** temporary code for global search */
    $route: {
      handler: function (to, from) {
        this.globalSearchKeyword = "";
        let name = to.query.name;
        if (name) {
          this.globalSearchKeyword = name;
        } else if(this.statusFilter) {
          //status filter in route need to be removed on switching account
          if(this.filterAccountId && (this.filterAccountId !== this.currentAccountId)) {
            this.$router.replace({'query': null}).catch(err => {});
          } else {
            this.applyDashboardFilters()
          }
        } else if(this.selectedFilterId && this.filterAccountId && (this.filterAccountId !== this.currentAccountId)) {
          //custom filter needs to be removed on switching account
          this.$store.dispatch("tickets/selectedFilterId", null);
          this.$store.dispatch("tickets/savedFilterId", null);
          this.$router.push({ name: 'tickets' }).catch(err => {})
        }
        // for edit view functionality
        if(to.params.id && to.name === 'custom-view') {
          this.$store.dispatch("tickets/selectedFilterId", to.params.id);
        } else {
          this.$store.dispatch("tickets/selectedFilterId", null);
        }
        // Fetch ticket list based on search
        if(name) 
          this.getTicketList();
        else
          this.checkTicketFilter()
      },
      deep: true,
      immediate: true,
    },
    'associatedField.statusList' (newvalue, oldValue) {
      if(this.statusFilter && (newvalue.length != oldValue.length)) {
        this.applyDashboardFilters()
        this.getTicketList()
      } 
    }
  },
  beforeRouteLeave(to, from, next) {
    // reset all the active filters before navigating to another route
    this.resetFilters();
    next();
  },
  methods: {
    enableTab(item) {
      this.$router.push({ name: item.routerName, params: { id: item.id }}).catch(err => {});
    },
    openCard() {
      this.$refs.customizeView.cardOpen = true
    },  
    openColumnDrawer() {
      this.$refs.columnFilterDrawer.drawerState = true;
    },
    updatePreferences(headers) {
      let payload = {
          "service_cloud": {
            "tickets": {
              "headers": headers
            }
          }
        }          
      $preferences.update(payload, {}).then((response) => {
        this.$store.dispatch("ui/snackbar", [
          {
            message: "Preferences Update Successfully",
            type: "success",
          }
        ]);
      }).catch((e)=> {
        console.log(e)
        this.$store.dispatch("ui/snackbar", [
          {
            message: 'Please try again',
            type: "error",
          }
        ]);
      })
    },
    filterSelectedColumns(columns) {
      this.headers = columns.filter(header => header.show === true);
      //to enable the filter icon at the end
      this.headers.push(this.filterAction)
      const modifiedHeaders = columns.filter(item => item.show).map(item => item.value);
      this.updatePreferences(modifiedHeaders)
    },  
    enableTop (id) {
      return commonFunctions.calculateTop(id, this.ticketList)
    },
    enableAttach () {
      return commonFunctions.enableAttach(this.totalItems)
    },
    unAssign(action,item) {
      switch (action) {
        case 'agent':
          this.performAction(item,'agent_id',null,'index-page');
          break;
        case 'type':
          this.performAction(item,'ticket_type_id',null,'index-page');
          break;
        default:
          break;
      }
    },
    getModified(tagName,maxLength) {
      return tagName.length > maxLength ? tagName.substring(0, maxLength) + '…' : tagName;
    },
    checkRouterAndAppliedFilter() {
      if (this.$route.name == "high-priority-tickets") {
        this.checkTicketFilter("priority_in");
      } else if (this.$route.name == "my-tickets") {
        this.checkTicketFilter("agent_id_in");
      } else if (this.$route.name == "custom-view") {
        this.checkTicketFilter("custom-view");
      } else {
        this.getTicketList();
      }
    },

    rowClass(item) {
      let rowClass = "table-row";
      if (item.status == "closed") {
        rowClass = "closed-row";
      }
      return rowClass;
    },

    /** this method invoked from tab click of page load */
    async checkTicketFilter() {
      switch(this.activeTabName){
        case 'high-priority-tickets':
          if(this.savedFilterId !== null){
            this.selectedListView = null;
            this.resetFilters();
          }
          this.getTicketList("priority_in", "fromTab", 2);
          break;
        case 'my-tickets':
          /** check if current loogedIn agent is present in agent list */
          await this.getAgentList(this.currentLoggedInUserEmail);
          this.associatedField.agentList.forEach((agent) => {
            if (agent.email == this.currentLoggedInUserEmail) {
              this.currentAgentId = agent.id;
            }
          });
          if(this.savedFilterId !== null){
            this.selectedListView = null;
            this.resetFilters();
          }
          this.getTicketList("agent_id_in", "fromTab", this.currentAgentId);
          break;
        
        case 'tickets':
          if(this.savedFilterId !== null){
            this.selectedListView = null;
            this.resetFilters();
          }
          this.getTicketList("all-tickets", "", null);
          break;
        case 'custom-view':
          if(this.selectedFilterId){
            this.filterListByView(this.selectedFilterId);
          } else {
            this.ticketList = [];
            this.totalItems = 0;
            this.loading = false;
          }
          break;
      }
    },

    async getTicketList(filterName = "", action = "", value = "") {
      if(this.savedFilterId === null){
        this.selectedListView = null;
      }
      if (action !== "fromTab") {
        this.loadFilter = true;
      } else {
        this.loadFilter = false;
      }

      let appliedFilter = this.$store.getters["tickets/appliedFilter"];
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const query = {
        q: {
          subject_or_id_cont: this.globalSearchKeyword,
        }
      }
      const data = {
        page,
        per_page: itemsPerPage,
        q: {...query.q},
      };
      if (Object.keys(appliedFilter).length !== 0 && action == "" && this.savedFilterId == null) {
        for (let key in appliedFilter) {
          data.q[key] = appliedFilter[key];
        }
        data.q["m"] = "and";
      } else if (this.savedFilterId != null && this.activeTabName === 'custom-view' && filterName !== 'all-tickets' && action !== "fromTab") {
        for (let key in appliedFilter) {
          data.q[key] = appliedFilter[key];
        }
        data.q["m"] = "and";
      } else if (action == "fromTab") {
        data.q[filterName] = [value];
        appliedFilter[filterName] = value
        this.$store.dispatch("tickets/appliedFilter", [appliedFilter]);
      } else {
        data.q = {...query.q}
      }
      if (sortBy && sortBy.length) {
        // If sorting ..
        const dir = sortDesc.length
          ? sortDesc[0] === true
            ? "desc"
            : "asc"
          : "";

        data.q.s = `${
          sortBy[0] == "agent_name"
            ? "agent_first_name"
            : sortBy[0] == "contact_name"
            ? "contact_first_name"
            : sortBy[0]
        } ${dir}`;
      }
      await $tickets
        .fetchList(data, {})
        .then((response) => {
          this.totalItems = response.data.pagination.total_count;
          this.ticketList = response.data.tickets;
          this.selectedTicketsData = [];
          this.loading = false;
          
        })
        .catch(() => {
          this.loading = false;
        });
    },

    getSelectData(data) {
      this.selectedTicketsData = data;
    },

    validateClose() {
      if (this.selectedTicketsData.length == 1) {
        this.$refs.form.validate();
        if (this.$refs.form.validate()) {
          this.confirmClose();
        }
      } else {
        this.confirmClose();
      }
    },
    /**
     * @Description
     * Display the delete confirmation modal box before actually deleting or trashing the ticket.
     */
    onDelete() {
      this.dialog.attrs.subtitle = `Are you sure you want to delete the selected ticket${ this.checkSelectedTicket > 1 ? "s" : "" }?`;
      this.dialog.attrs.show = true;
    },
    /**
     * @description
     * Handles the confirmation of ticket deletion by first initiating the deletion process,
     * indicating loading status, and then closing the dialog box.
     */
    onConfirm() {
      if (this.selectedTicketsData.length !== 0) {
        this.dialog.attrs.loading = true;

        // Manage payload for tickets trash API Call
        const payload = {
          ids: this.selectedTicketsData.map(ticket => ticket.id)
        }

        $tickets.trash(payload, {}).then((response) => {
          this.$store.dispatch("ui/snackbar", [{
            message: `Ticket${this.checkSelectedTicket > 1 ? "s" : ""} deleted successfully.`,
            type: "success",
          }]);
          this.checkRouterAndAppliedFilter();
        }).catch((error) => {
          this.$store.dispatch("ui/snackbar", [
            {
              message: this.$errors(error.response.data).msg({
                capitalize: false,
                prepend: `${this.$errors(
                  error.response.data
                ).subjectText()} `,
              }),
              type: "error",
            },
          ]);
        }).finally(() => {
          this.dialog.attrs.loading = false;
          this.dialog.attrs.show = false;
        })
      }
    },

    async checkRelevantAction(ticket, name, status, page) {
      if (name === "status") {
        if (ticket.status !== status.name) {
          // temporarily commenting this block as part of MPUP-1870
          // if (status.name == "closed") {
          //   this.closeAction(ticket, "update-status");
          // } else {
            await this.performAction(ticket, name, status.value, page);
          // }
        }
      } else if(name === "agent_id") {
          this.performAction(ticket, name, status, page);
          this.hideAgentDropdown = true;
          this.enteredAgent = null
      }
    },

    assignTicket() {
      this.selectedTicketsData.forEach((ticket) => {
        this.performAction(
          ticket,
          "agent_id",
          this.selectedAgent,
          "index-page",
          "assign-ticket"
        );
      });
      this.assignTicketDialog = false;
      this.selectedAgent = "";
    },

    assignTicketToggle() {
      this.assignTicketDialog = true;
    },
    applyDashboardFilters() {
      let statusList =  this.associatedField.statusList
      let filteredStatus = statusList.filter((status) => {
        if(this.statusFilter === 'unresolved') {
          return status.name !== 'closed'
        } else if (this.statusFilter === 'pending_on_hold') {
          return status.name === 'pending' || status.name === 'on_hold'
        } else {
          return status.name === this.statusFilter
        }
      }).map(obj => obj.value);
      if(filteredStatus.length > 0) {
        let data = {
          channel: [],
          dates: [],
          group: [],
          priority: [],
          selectedAgent: [],
          selectedContact: [],
          tags: [],
          status: filteredStatus,
          type: []
        }
        let appliedFilter = {
          status_in: filteredStatus
        };
        this.$store.dispatch("tickets/appliedFilter", [appliedFilter]);
        this.$store.dispatch("tickets/filterData", [data]);
        this.$store.dispatch("tickets/filterAccountId", this.currentAccountId);
      }
    },
    /**
     * Function to add new filter drawer.
     */
    openAddNewViewDrawer(){
      this.pageLoader = true;
      this.resetFilters();
      this.loadFilter = true;
      this.$nextTick(() => {
        this.pageLoader = false;
        this.$refs.ticketFilterDrawer.toggleFilterDrawer('ADD');
      });
    },
    /**
     * Function to filter tickets data by saved view.
     * @param {*} viewId 
     */
    async filterListByView(viewId){
      this.pageLoader = true;
      this.$store.dispatch("tickets/savedFilterId", viewId);
      this.$store.dispatch("tickets/selectedFilterId", viewId);
      this.$store.dispatch("tickets/filterMode", 'VIEW');
      //filter account ID needs to be updated while applying filters
      this.$store.dispatch("tickets/filterAccountId", this.currentAccountId);
      try {
        const response = await $filter.fetchFilter(viewId);
        const savedViewData = response.data
        let filterData = {
          status: savedViewData.query.status_in,
          priority: savedViewData.query.priority_in,
          channel: savedViewData.query.channel_in,
          type: savedViewData.query.ticket_type_id_in,
          group: savedViewData.query.group_id_in,
          selectedContact: savedViewData.query.contact_id_in,
          selectedAgent: savedViewData.query.agent_id_in,
          tags: savedViewData.query.tags_id_in,
        };
        
        let dates = [];
        if(savedViewData.query.created_at_gteq){
          dates.push(filterData.created_at_gteq);
        } 
        
        if(savedViewData.query.created_at_lteq) {
          if(savedViewData.query.created_at_gteq !== savedViewData.query.created_at_lteq){
            dates.push(savedViewData.query.created_at_lteq);
          }
        }
        filterData.dates = dates;
        await this.setAppliedFilter(savedViewData);
        this.$store.dispatch("tickets/filterData", [filterData]);
        this.pageLoader = false;
        this.getTicketList('custom-view');
        if(this.$refs.ticketFilterDrawer)
          this.$refs.ticketFilterDrawer.checkFilterCount();
      } catch (error) {
        // reset filters
        this.$store.dispatch("tickets/selectedFilterId", null);
        this.$store.dispatch("tickets/savedFilterId", null);
        this.$router.push({ name: 'tickets' }).catch(err => {})
      }
    
    },
    setAppliedFilter(savedViewData) {
      let groupName = "group_id_in";
      let typeName = "ticket_type_id_in";
      let agentName = "agent_id_in";
      let tagName = "tags_id_in"
      if (savedViewData.query.group_id_in && savedViewData.query.group_id_in[0] == "blank") groupName = "group_id_blank";
      if (savedViewData.query.ticket_type_id_in && savedViewData.query.ticket_type_id_in[0] == "blank") {
        typeName = "ticket_type_id_blank";
      }
      if (savedViewData.query.agent_id_in && savedViewData.query.agent_id_in[0] == "blank") {
        agentName = "agent_id_blank";
      }
      if (this.tags && this.tags[0] == "blank") {
        tagName = "tag_id_blank";
      }

      let data = {
        status_in: savedViewData.query.status_in,
        priority_in: savedViewData.query.priority_in,
        channel_in: savedViewData.query.channel_in,
        [typeName]: typeName === 'ticket_type_id_blank' ? true : savedViewData.query.ticket_type_id_in,
        [groupName]: groupName === 'group_id_blank' ? true : savedViewData.query.group_id_in,
        [agentName]: agentName === 'agent_id_blank' ? true : savedViewData.query.agent_id_in,
        [tagName]: savedViewData.query.tags_id_in && savedViewData.query.tags_id_in[0] == "blank" ? true : savedViewData.query.tags_id_in,

        created_at_gteq: savedViewData.query.created_at_gteq,
        created_at_lteq: savedViewData.query.created_at_lteq,
        contact_id_in: savedViewData.query.contact_id_in,
      };
      return this.$store.dispatch("tickets/appliedFilter", [data]);
    },
    /**
     * Function to open filter drawer on update mode.
     * @param {*} viewId 
     */
    editFilterView(viewId){
      this.pageLoader = true;
      this.resetFilters();
      this.loadFilter = true;
      this.$nextTick(() => {
        this.$store.dispatch("tickets/savedFilterId", viewId);
        this.$store.dispatch("tickets/filterMode", 'EDIT');
        this.pageLoader = false;
        this.$refs.ticketFilterDrawer.toggleFilterDrawer();
      });
    },
    /**
     * Open delete confirmation dialog before delete any saved filter.
     * @param {*} viewId 
     */
    deleteFilterView(viewId){
      this.$refs.deleteViewDialog.openDialog(viewId);
    },
    /**
     * Method to execute processes after delete view operation.
     * Event emitted from delete view dialog component. 
     */
    onCloseDeleteViewDialog(){
      this.refreshList();
      this.resetFilters();
      this.getTicketList();
    },
    resetFilters(){
      this.$store.dispatch("tickets/appliedFilter", [{}]);
      if(this.$refs.ticketFilterDrawer){
        this.$refs.ticketFilterDrawer.reSetFilter();
      }
    },

    /**
     * Redirection to ticket detail page on clicking of ticket row
     */
    rowClickHandler(item){
      this.$router.push({  
        name: 'ticket-details', params: { id: item.id 
      }}).catch(err => {});
    },

    /**
     * Redirection to contact dashboard page on clicking of contact name
     */
    redirectToContact(item) {
      this.$router
        .push({
          name: "contact-dashboard",
          params: { accountId: this.account.id, contactId: item.contact_id },
        })
        .catch((err) => {});
    },
  } 
};
</script>

<style lang="scss">
tr.closed-row td,
tr.closed-row td a {
  color: #bdbdbd !important;
}

.tabs-row {
  align-items: center !important;
}
.ticket-list-table td.text-right,
.ticket-list-table th.text-right {
  text-align: left !important;
}
.subject-col {
  min-width: 200px !important;
}

.custom-dropdown {
  min-width: 100px;
  display: block;
}

.custom-select-box .v-menu__content--fixed {
  top: 200px !important;
}
.v-menu__content .active-view-item{
  background-color: rgba(0, 0, 0, 0.1) !important;
}
.custom-view-list-container{
  max-height:192px;
  overflow:auto;
}

.item-add-custom-view{
  color: #006BAF;
}
.quick-filter {
  cursor: pointer;
  border-bottom: none !important;
}
.view-container {
  position: relative;
}
.active-menu-item {
  border-bottom: 2px solid;
}

.tabs-section {
  width: 95%;
  .v-slide-group__next, .v-slide-group__prev {
    flex: 0 1 30px;
    min-width: 30px;
  }
}

.ticket-list-table {
  th.fixed {
    position: sticky;
    right: 0;
    top: auto;
    padding: 0px!important;
  }
  
  .filter-icon {
    background: white;
  }
}

.custom-view-title {
  cursor: pointer;
  text-transform: none;
}

.search-agent {
  border-bottom: 2px solid;
  .v-input__slot {
    margin-bottom: 0px !important;
  }
}

.menu-list {
  max-height: 150px; 
  max-width: 200px
}
.link-color{
  color: #006baf !important;
}
</style>
